import {
  type OrderRateReference_Request,
  patchV1CompaniesCompanyIdOrdersOrderIdRateReference,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import connection from '~services/connectionModule';

// Where each of the project's records landed on the order, keyed by the SOURCE id: a copy
// records no back-pointer on either grain, so this map is the only way a caller can redirect
// edits it composed against the project's cards onto the order's own copies.
interface OrderRateReferenceResult {
  clonedAutoAddOnAssignments: Record<string, string>;
  clonedSiteRateSchedules: Record<string, string>;
  defaultSiteRateScheduleId: string | null;
  dereferenced: boolean;
}

interface ApplyOrderRateReferenceArgs {
  companyId: string;
  orderId: string;
  // `true` decouples the order from its project's rate cards; `false` re-attaches it.
  dereferenced: boolean;
}

/**
 * Decouples ONE order from its project's rate book, independent of any rate-card write.
 *
 * The order's starred card MUST already be persisted when this runs. The backend repoints the
 * star onto the copy as it clones, so a star that lands afterwards still names a PROJECT card —
 * which the resolver honours over the order's own copies, leaving a decoupled order priced off
 * the project with no cascade to correct it.
 *
 * Must also run BEFORE the card writes: the backend skips the clone for an order that already
 * owns cards, and a copy written after the user's own card loses the oldest-wins tie for the
 * same condition tuple.
 *
 * Reports the order's RESULTING state, so a retry after a half-failed save returns the same
 * maps rather than an empty one.
 */
export async function applyOrderRateReference({
  companyId,
  orderId,
  dereferenced,
}: ApplyOrderRateReferenceArgs): Promise<OrderRateReferenceResult> {
  // `OrderRateReference_Request` is `additionalProperties:false` — typing the literal makes the
  // strict-key invariant compiler-enforced rather than a runtime 4xx.
  const body: OrderRateReference_Request = { dereferenced };

  try {
    const response = await patchV1CompaniesCompanyIdOrdersOrderIdRateReference({
      path: { 'company-id': companyId, 'order-id': orderId },
      body,
      throwOnError: true,
    });

    const data = response.data?.data;
    return {
      clonedAutoAddOnAssignments: data?.cloned_auto_add_on_assignments ?? {},
      clonedSiteRateSchedules: data?.cloned_site_rate_schedules ?? {},
      defaultSiteRateScheduleId: data?.default_site_rate_schedule_id ?? null,
      dereferenced: data?.dereferenced ?? false,
    };
  } catch (e) {
    // `handleRequestError` toasts, reports to Sentry, and ALWAYS throws, so swallow that throw
    // and re-throw the ORIGINAL error for the caller to abort on.
    try {
      connection.handleRequestError(
        e,
        t('error_messages.orders.failed_to_stop_tracking_project_rates'),
      );
    } catch {
      // Error already handled (toast + Sentry)
    }
    throw e;
  }
}
